export const MAP_LOCATION_TOPIC = '/visualization/device-locations/observed'
const id = /^[A-Za-z0-9_-]{1,64}$/

// 保存声明式范围，页面产品仅在运行时解析；预览产品不进入发布或预览快照。
export function locationParameter (parameter = {}) {
  if (!parameter || typeof parameter !== 'object' || Array.isArray(parameter)) throw new Error('地图设备范围无效')
  const scope = parameter.scope || 'all', state = parameter.state || 'all'
  if (!['all', 'products', 'devices', 'current', 'device'].includes(scope) || !['all', 'online', 'offline'].includes(state)) throw new Error('地图设备范围无效')
  const ids = key => {
    const values = parameter[key] || []
    if (!Array.isArray(values) || values.length > 200 || values.some(value => typeof value !== 'string' || !id.test(value))) throw new Error('地图产品或设备编号无效，最多选择200项')
    return Array.from(new Set(values))
  }
  const productIds = scope === 'products' ? ids('productIds') : [], deviceIds = scope === 'devices' ? ids('deviceIds') : []
  if ((scope === 'products' && !productIds.length) || (scope === 'devices' && !deviceIds.length)) throw new Error('请先选择地图展示的产品或设备')
  const propertyId = parameter.propertyId || ''
  if (typeof propertyId !== 'string' || (propertyId && !/^[A-Za-z0-9_.:\u4e00-\u9fff-]{1,128}$/.test(propertyId))) throw new Error('定位属性编号无效')
  return { scope, productIds, deviceIds, propertyId, state }
}

export function locationQuery (parameter = {}, context = {}, editing = false) {
  const query = locationParameter(parameter)
  if (query.scope === 'current') {
    const productId = editing ? parameter.previewProductId : context && context.productId
    if (typeof productId !== 'string' || !id.test(productId)) throw new Error(editing ? '请选择编辑时使用的示例产品' : '当前页面没有有效产品 ID，请从产品列表进入')
    return { ...query, scope: 'products', productIds: [productId] }
  }
  if (query.scope === 'device') {
    const deviceId = editing ? parameter.previewDeviceId : context && context.deviceId
    if (typeof deviceId !== 'string' || !id.test(deviceId)) throw new Error(editing ? '请选择编辑时使用的示例设备' : '当前页面没有有效设备 ID，请从设备列表进入')
    return { ...query, scope: 'devices', deviceIds: [deviceId] }
  }
  return query
}

export function locationPoint (value) {
  if (!value || !id.test(value.deviceId || '') || !id.test(value.productId || '') ||
    !Number.isFinite(value.lng) || Math.abs(value.lng) > 180 || !Number.isFinite(value.lat) || Math.abs(value.lat) > 90 ||
    !Number.isSafeInteger(value.timestamp) || value.timestamp < 0) throw new Error('平台设备位置格式无效')
  return { deviceId: value.deviceId, productId: value.productId, propertyId: value.propertyId || '',
    name: typeof value.name === 'string' ? value.name.slice(0, 256) : value.deviceId,
    lng: value.lng, lat: value.lat, timestamp: value.timestamp,
    source: ['tag', 'legacy'].includes(value.source) ? value.source : 'property',
    state: ['online', 'offline'].includes(value.state) ? value.state : 'unknown' }
}

// 同一设备分别维护位置时间和连接时间，避免在线事件覆盖较新的位置或反过来。
export function createLocationStore (query) {
  let points = new Map(), connections = new Map()
  const permitted = point => query.scope === 'all' || (query.scope === 'products' ? query.productIds.includes(point.productId) : query.deviceIds.includes(point.deviceId))
  const apply = change => {
    if (change.kind === 'state') {
      if (!id.test(change.deviceId || '') || !['online', 'offline'].includes(change.state) || !Number.isSafeInteger(change.timestamp)) throw new Error('地图设备状态格式无效')
      const old = connections.get(change.deviceId)
      if (!old || change.timestamp >= old.timestamp) connections.set(change.deviceId, { state: change.state, timestamp: change.timestamp })
    } else {
      const point = locationPoint(change)
      if (!permitted(point)) throw new Error('地图返回了筛选范围外的设备')
      const old = points.get(point.deviceId)
      const rank = row => row.source === 'property' ? 2 : row.source === 'tag' ? 1 : 0
      if (!old || rank(point) > rank(old) || (rank(point) === rank(old) && point.timestamp >= old.timestamp)) points.set(point.deviceId, point)
    }
  }
  return {
    change: apply,
    snapshot (rows, pending = [], preserve = false) {
      if (!Array.isArray(rows)) throw new Error('地图位置快照无效')
      const previous = preserve ? [...points.values(), ...Array.from(connections, ([deviceId, value]) => ({ kind: 'state', deviceId, ...value }))] : []
      points = new Map(); connections = new Map()
      rows.forEach(apply); previous.forEach(apply); pending.forEach(apply)
    },
    changes () { return [...points.values(), ...Array.from(connections, ([deviceId, value]) => ({ kind: 'state', deviceId, ...value }))] },
    rows () { return Array.from(points.values()).map(point => ({ ...point, state: (connections.get(point.deviceId) || point).state }))
      .filter(point => query.state === 'all' || point.state === query.state) }
  }
}

export function locationFrame (frame) {
  if (!frame || frame.protocolVersion !== 1 || !['ready', 'changes', 'resync', 'snapshot'].includes(frame.kind)) throw new Error('地图位置协议不一致，请更新后端')
  if (frame.kind === 'changes' && (!Array.isArray(frame.changes) || frame.changes.length > 100)) throw new Error('地图实时位置批次无效')
  return frame
}

// 静态演示只在显式页面范围时筛选；无页面参数可用保存的编辑示例，不发起平台查询。
export function staticLocationRows (rows, parameter = {}, context = {}, editing = false) {
  if (!Array.isArray(rows)) return []
  const scope = parameter.scope || 'all'
  if (scope === 'all') return rows
  if (scope === 'products') return rows.filter(row => Array.isArray(parameter.productIds) && parameter.productIds.includes(row.productId))
  if (scope === 'devices') return rows.filter(row => Array.isArray(parameter.deviceIds) && parameter.deviceIds.includes(row.deviceId))
  if (scope === 'current' || scope === 'device') {
    const device = scope === 'device', key = device ? 'deviceId' : 'productId', preview = device ? 'previewDeviceId' : 'previewProductId'
    const selected = editing ? parameter[preview] : context[key] || parameter[preview]
    return typeof selected === 'string' && id.test(selected) ? rows.filter(row => row[key] === selected) : []
  }
  return []
}
